//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  ICreateUpdateScheduledPost,
  IScheduledInstagramPost,
  IScheduledPost,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import DateString from '@models/DateString';
import DraftId from '@models/DraftId';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { SocialNetwork } from '@models/SocialNetwork';
import { PostDetailsValidationError } from '@universal-scheduling/components/calendar/UniversalCalendarConstants';
import { WeekDayValue } from '@utils/moment';
import { Normalized } from '@utils/normalize';

export interface IUniversalCalendarState {
  // UI
  scheduleView: UniversalCalendarView;
  startOfWeek: WeekDayValue;

  // Data
  scheduledPosts: NormalizedScheduledPosts;
  draftPosts: NormalizedDraftPosts;

  availableNetworks: SocialNetwork[];
  disconnectedAvailableNetworks: SocialNetwork[];

  contentTypeFilter: ContentTypeFilter | 'All';
  networkFilter: SocialNetwork | 'All';
  searchText: string;

  // Configuration
  dateRange: {
    startDate: DateString;
    endDate: DateString;
  };

  postStatusFilter: PostStatusFilter | 'All';
  modals: {
    isDeletePostModalOpen: boolean;
    isPostDetailsModalOpen: boolean;
    currentPost: IScheduledPost | IDraftPost | undefined;
    editedPost: IScheduledPost | IDraftPost | undefined;
    createdPosts: (IScheduledPost | IDraftPost)[] | undefined;
    previewCuidIndex: number;
    validationErrors: PostDetailsValidationError[];
  };

  flags: {
    isDeletingPost: boolean;
    isFetchingCalendarSettings: boolean;
    isFetchingScheduledPosts: boolean;
    isSettingCalendarSettings: boolean;
    isUpdatingPostDetails: boolean;
    isUpdatingPostScheduledTime: boolean;
  };
  errors: {
    deletePostError: string | undefined;
    fetchCalendarSettingsError: string | undefined;
    fetchScheduledPostsError: string | undefined;
    setCalendarSettingsError: string | undefined;
    updatePostDetailsError: string | undefined;
    updatePostScheduledTimeError: string | undefined;
  };
}

export interface IUniversalTimelinePostGroup {
  date: DateString;
  posts: (IScheduledPost | IDraftPost)[];
}

export interface IScheduleCalendarSettings {
  scheduleCalendarStartOfWeek?: WeekDayValue;
  scheduleCalendarView?: UniversalCalendarView;
}

export enum UniversalCalendarView {
  Week = 'Week',
  Timeline = 'Timeline',
  Month = 'Month',
}

export type NormalizedScheduledPosts = Normalized<IScheduledPost, PostableId>;
export type NormalizedDraftPosts = Normalized<IDraftPost, DraftId>;
export type NormalizedInstagramLivePosts = Normalized<IInstagramPost, string>;

export const allPostStatuses: PostableStatus[] = [
  PostableStatus.Dispatched,
  PostableStatus.Failed,
  PostableStatus.Pending,
  PostableStatus.Posted,
  PostableStatus.Processing,
  PostableStatus.ProcessingComplete,
  PostableStatus.Scheduled,
];

export const scheduledPostStatuses: PostableStatus[] = [
  PostableStatus.Dispatched,
  PostableStatus.Failed,
  PostableStatus.Pending,
  PostableStatus.Processing,
  PostableStatus.ProcessingComplete,
  PostableStatus.Scheduled,
];

export const postedPostStatuses: PostableStatus[] = [
  PostableStatus.Posted,
  PostableStatus.ProcessingComplete,
];

export const allScheduleNetworks: SocialNetwork[] = [
  SocialNetwork.Facebook,
  SocialNetwork.Instagram,
  SocialNetwork.Pinterest,
  SocialNetwork.Twitter,
  SocialNetwork.TikTok,
  SocialNetwork.YouTube,
];

export enum PostStatusFilter {
  Scheduled = 'Scheduled', // Backend (post status)
  Posted = 'Posted', // Backend (post status)
  Draft = 'Draft', // Frontend
  Manual = 'Manual', // Backend (posting group)
}

export enum ContentTypeFilter {
  Photo = 'Photo',
  Video = 'Video',
  Carousel = 'Carousel',
  Text = 'Text',
  Story = 'Story',
}

export const SCHEDULED_POST_FETCH_LIMIT = 50;

export interface ScheduleOptions {
  postTimeMs?: number;
  copiedScheduledPostId?: PostableId;
  copiedDraftId?: DraftId;
  draftPostId?: undefined;
  scheduledPostId?: undefined;
}
